/**
 * Hard size limits (bytes) enforced on BOTH the client and the server.
 * The server rejects oversized encrypted payloads even if malformed.
 */
export const PROTOCOL_VERSION = 1 as const;

export const SIZE_LIMITS = {
  text: 1 * 1024 * 1024, // 1 MB
  image: 4 * 1024 * 1024, // 4 MB
  file: 10 * 1024 * 1024, // 10 MB (offer declares the real size for display)
  "file-data": 10 * 1024 * 1024, // 10 MB (on-demand encrypted bytes)
  meta: 10 * 1024, // 10 KB
  control: 1 * 1024, // 1 KB
} as const;

/** Files larger than this are split into {@link FILE_CHUNK_SIZE} pieces for
 *  transport (§12.1); smaller files use a single envelope. */
export const FILE_CHUNK_THRESHOLD = 4 * 1024 * 1024; // 4 MB
export const FILE_CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB
/** Hard cap on chunk count (defense against a malicious count field). */
export const MAX_FILE_CHUNKS = 1024;

export type LimitedType = keyof typeof SIZE_LIMITS;

/**
 * AES-GCM adds a 16-byte tag and we base64url-encode ciphertext (~4/3 inflation).
 * This is the maximum accepted encoded ciphertext length for any single field.
 */
export const CIPHERTEXT_OVERHEAD = 4096;
export const MAX_CIPHERTEXT_BYTES =
  Math.ceil((SIZE_LIMITS.file * 4) / 3) + CIPHERTEXT_OVERHEAD;

/** Pairing bootstrap codes are single-use and short lived. */
export const PAIRING_TTL_MS = 5 * 60 * 1000; // 5 minutes
export const PAIRING_MAX_ATTEMPTS = 5; // lockout after N invalid proofs

export function limitForType(type: LimitedType): number {
  return SIZE_LIMITS[type];
}

/**
 * Returns true when the declared plaintext `size` is within the limit for its
 * payload `type`. Types without a plaintext-size concept default to control.
 */
export function withinPlaintextLimit(type: LimitedType, size: number): boolean {
  if (!Number.isFinite(size) || size < 0) return false;
  return size <= SIZE_LIMITS[type];
}
